/** REACT_APP_STRAPI_URL from apps/web/.env at build time; fallback same-origin (nginx /api/). */
function resolveStrapiUrl(): string {
  const fromEnv = (process.env.REACT_APP_STRAPI_URL ?? '').replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined') return window.location.origin;
  return 'http://localhost:1337';
}

export const STRAPI_URL = resolveStrapiUrl();

/** Absolute URL for a Strapi upload (`/uploads/...`) so <img> can load it. */
export function strapiMediaUrl(url: string): string {
  const v = url.trim();
  if (!v) return '';
  if (v.startsWith('data:') || v.startsWith('blob:') || /^https?:\/\//i.test(v)) return v;
  const path = v.startsWith('/') ? v : `/${v}`;
  return `${STRAPI_URL}${path}`;
}

/** Use in TanStack Query `queryFn` when calling authenticated Strapi routes */
export function strapiAuthHeaders(jwt: string): HeadersInit {
  return { Authorization: `Bearer ${jwt}` };
}

export type StrapiUser = {
  id: number;
  /** Strapi Document Service id (users-permissions); untuk cocokkan assignee quotation. */
  documentId?: string | null;
  username: string;
  email: string;
  blocked?: boolean;
  confirmed?: boolean;
  /** Populated from `user_type` when calling `/api/users/me?populate[user_type]=true`. */
  userTypeCode?: string | null;
  userTypeName?: string | null;
  /** Titles of assigned `analis_type_data` rows, when populated. */
  analisTypeDataTitles?: string[];
};

function unwrapPopulateOneEntry(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object') return null;
  if ('data' in v) {
    const d = (v as { data: unknown }).data;
    if (d && typeof d === 'object' && !Array.isArray(d)) return d as Record<string, unknown>;
    return null;
  }
  if (Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function relationRecords(v: unknown): Record<string, unknown>[] {
  if (Array.isArray(v)) return v.flatMap((item) => relationRecords(item));
  if (!v || typeof v !== 'object') return [];
  if ('data' in v) return relationRecords((v as { data: unknown }).data);
  return [v as Record<string, unknown>];
}

function userTypeLabelsFromRawUser(raw: Record<string, unknown>): {
  userTypeCode: string | null;
  userTypeName: string | null;
  analisTypeDataTitles: string[];
} {
  const src = flattenStrapiEntry(raw);
  const utWrap = unwrapPopulateOneEntry(raw.user_type ?? src.user_type);
  const typeLabels = utWrap
    ? (() => {
        const uf = flattenStrapiEntry(utWrap);
        return {
          userTypeCode: String(uf.type_code ?? '') || null,
          userTypeName: String(uf.type_name ?? '') || null,
        };
      })()
    : { userTypeCode: null, userTypeName: null };
  const analisTypeDataTitles: string[] = [];
  for (const row of relationRecords(raw.analis_type_data ?? src.analis_type_data)) {
    const title = String(flattenStrapiEntry(row).title ?? '').trim();
    if (title && !analisTypeDataTitles.includes(title)) analisTypeDataTitles.push(title);
  }
  return { ...typeLabels, analisTypeDataTitles };
}

export type AuthResponse = {
  jwt: string;
  user: StrapiUser;
};

function strapiFieldErrorLines(details: unknown): string[] {
  if (!details || typeof details !== 'object' || !('errors' in details)) return [];
  const errors = (details as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return [];
  const lines: string[] = [];
  for (const item of errors) {
    if (!item || typeof item !== 'object') continue;
    const row = item as { path?: unknown; message?: unknown };
    const path = Array.isArray(row.path)
      ? row.path.map((part) => String(part)).filter((part) => part !== '').join('.')
      : typeof row.path === 'string'
        ? row.path.trim()
        : '';
    const message = typeof row.message === 'string' ? row.message.trim() : '';
    if (path && message) lines.push(`${path}: ${message}`);
    else if (message) lines.push(message);
    else if (path) lines.push(path);
  }
  return lines;
}

export function parseErrorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const err = (body as { error?: { message?: string; name?: string; details?: unknown } }).error;
    const fieldLines = strapiFieldErrorLines(err?.details);
    if (fieldLines.length > 0) return fieldLines.join('\n');
    if (err?.message) return err.message;
    if (err?.name) return err.name;
  }
  return `Request failed (${status})`;
}

export async function strapiLogin(identifier: string, password: string): Promise<AuthResponse> {
  const res = await fetch(`${STRAPI_URL}/api/auth/local`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, password }),
  });
  const data = (await res.json()) as AuthResponse & { error?: { message?: string } };
  if (!res.ok) {
    throw new Error(parseErrorMessage(data, res.status));
  }
  if (!data.jwt || !data.user) {
    throw new Error('Invalid response from Strapi');
  }
  return data;
}

export async function strapiRegister(input: {
  username: string;
  email: string;
  password: string;
}): Promise<AuthResponse> {
  const res = await fetch(`${STRAPI_URL}/api/auth/local/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = (await res.json()) as AuthResponse & { error?: { message?: string } };
  if (!res.ok) {
    throw new Error(parseErrorMessage(data, res.status));
  }
  if (!data.user) {
    throw new Error('Invalid response from Strapi');
  }
  return data;
}

export function flattenStrapiEntry(
  item: Record<string, unknown>,
): Record<string, unknown> & { documentId?: unknown } {
  const attrs = item.attributes as Record<string, unknown> | undefined;
  return attrs ? { ...attrs, documentId: item.documentId } : item;
}

export async function strapiFetchMe(jwt: string): Promise<StrapiUser> {
  const params = new URLSearchParams({
    'populate[user_type]': 'true',
    'populate[analis_type_data]': 'true',
  });
  const res = await fetch(`${STRAPI_URL}/api/users/me?${params}`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  const data = (await res.json()) as Record<string, unknown> & StrapiUser & {
    error?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(parseErrorMessage(data, res.status));
  }
  const { userTypeCode, userTypeName, analisTypeDataTitles } = userTypeLabelsFromRawUser(data);
  const flat = flattenStrapiEntry(data);
  const documentId =
    data.documentId != null
      ? String(data.documentId)
      : flat.documentId != null
        ? String(flat.documentId)
        : null;
  return {
    id: Number(data.id),
    documentId,
    username: String(data.username ?? ''),
    email: String(data.email ?? ''),
    blocked: data.blocked != null ? Boolean(data.blocked) : undefined,
    confirmed: data.confirmed != null ? Boolean(data.confirmed) : undefined,
    userTypeCode,
    userTypeName,
    analisTypeDataTitles,
  };
}
